import React, {useState, useEffect} from "react";
import Link from 'next/link';
import _ from "lodash";

import {$store} from "@isudio/globals";
import {pages, policies} from "@isudio/config";
import {$core} from "@isudio/core";
import {cn, useRxSync} from "@isudio/utils";
import type {ISignupInvite} from "@isudio/data";
import {LoadingSpinner, RowContainer, RowWrapper, RowFlex, RowGrid, InputValidator, GradientButton, StringList, SocialIcons, useOpenComponent} from "../../";

interface PanelProps {
  panelRef?: React.RefObject<HTMLDivElement>;
  className?: string;
  panelCaption?: string;
  visible?: boolean;
}

const Panel = React.forwardRef<Element, PanelProps>(
  ({
     panelRef,
     className = "panel",
     panelCaption = "Our business is to make your business successful.",
     visible = true
   }
  ) => {
    const [loading, setLoading] = useState(false);
    const emailRef = React.useRef<HTMLInputElement>(null);
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const userPolicy = _.get($store, `Data.userPolicy$.value.fields`, policies.userPolicy.fields);
    const [email, setEmail] = React.useState("");
    const [error, setError] = React.useState<string | undefined>(undefined);
    const user: any = useRxSync($store.User.user$);
    const [userAvailable, setUserAvailable] = React.useState(!!user);
    const openComponent = useOpenComponent();

    const isValid =
      email.trim().length > 0 &&
      error === undefined;

    const links = pages.panel.links.map(l => {
      return _.omitBy({
        type: l.type,
        icon: SocialIcons[l.type as keyof typeof SocialIcons],
        href: l.type === 'contact' ? "#" : l.link,
        handler: l.type === "contact" ? openContact : undefined
      }, _.isNil);
    });

    const onSubscribe = async () => {
      if (!emailRef.current) return;
      const email = emailRef.current.value;
      if (!email) {
        setError("Email is required");
        return;
      }

      const payload = {
        params: {email: email}
      };

      setLoading(true);
      await $core.sendInvite(payload).then((_data: ISignupInvite) => {
        setLoading(false);
        _.toastSuccess(pages.panel.subscribeSuccessMessage);
        emailRef.current!.value = "";

      }).catch((e: any) => {
        setLoading(false);
        _.toastError(_.get(e, 'message'));
      });

      const t = setTimeout(async () => {
        emailRef?.current?.focus();
        clearTimeout(t);
      }, 10);
    }

    const openSendBrief = async () => {
      openComponent("sendBrief")
    };

    const validateEmail = (value: string): string | undefined => {
      const email = value.trim();

      if (!email) {
        return "Email is required.";
      }

      if (email.length < userPolicy.email.minLength) {
        return `Email should have at least ${userPolicy.email.minLength} characters.`;
      }

      if (email.length > userPolicy.email.maxLength) {
        return `Email can't exceed ${userPolicy.email.maxLength} characters.`;
      }

      if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) {
        return "Please enter a valid email address.";
      }

      return undefined;
    };

    const openContact = async () => {
      openComponent("contactUs");
    };

    useEffect(() => {
      setUserAvailable(!!user);
    }, [user]);

    const loadingSpinner = (
      <LoadingSpinner
        className="absolute"
      />
    );

    return (
      (visible &&
        <RowContainer
          containerRef={panelRef}
          className={cn(
            `relative grid px-[5%] md:px-[10%] py-8`,
            `bg-linear-to-b from-custom-blue-from from-1% via-custom-blue-via via-60% to-custom-blue-to to-100%`, className
          )}
        >
          {loading && visible ? loadingSpinner : null}

          <RowWrapper
            className="row-wrapper flex mx-auto w-full">
            <RowFlex
              className="gap-8 justify-between">
              <div
                className="flex flex-col gap-4 shrink-0 items-center justify-center">
                <div
                  className="max-w-120 whitespace-pre-line font-bold text-center text-sm md:text-md xl:text-xl text-white text-shadow-[1px_2px_1px_rgba(0,0,0,1)]">
                  {panelCaption}
                </div>

                {!userAvailable ? (
                  <RowGrid className="row w-fit gap-4 grid grid-cols-1 sm:grid-cols-[1fr_auto] items-center">
                    <InputValidator
                      ref={emailRef}
                      className="panel-input-field"
                      labelClass="text-bluegray-lightest"
                      placeholder="Email*"
                      minLength={userPolicy.email.minLength}
                      maxLength={userPolicy.email.maxLength}
                      mask={/[a-zA-Z0-9@._%+-]/}
                      format="email"
                      error={error}
                      showToast={_.toastError}
                      onChange={(e) => {
                        const value = e.target.value;

                        setEmail(value);
                        setError(validateEmail(value));
                      }}
                      onKeyDown={async (e) => {
                        if (e.key === "Enter") {
                          await onSubscribe();
                        }
                      }}
                    />
                    <GradientButton
                      ref={buttonRef}
                      tabIndex={-1}
                      type="button"
                      text="Subscribe"
                      className={
                        cn(
                          `justify-self-center btn-signup btn-${isValid ? "blue transition duration-200 hover:text-generic-hover hover:scale-105" : "gray"}`
                        )}
                      disabled={!isValid}
                      onClick={onSubscribe}
                    />
                  </RowGrid>
                ) : (
                  <GradientButton
                    ref={buttonRef}
                    tabIndex={-1}
                    type="button"
                    text="Send Brief"
                    className={
                      cn(
                        `justify-self-center btn-sendBrief btn-blue transition duration-200 hover:text-generic-hover hover:scale-105`
                      )}
                    onClick={openSendBrief}
                  />
                )}
              </div>

              <div
                className="flex flex-col gap-4 shrink-0 items-center justify-center min-w-[25%]">
                <div
                  className="logo w-fit h-fit overflow-hidden border-none">
                  <img
                    src="/assets/img/port/logo-white.png"
                    alt="Sudio Advisory"
                    className="w-28 h-auto xl:w-40 object-contain border-none"
                  />
                </div>
                <div className="flex w-fit h-fit gap-4 text-xs">
                  <Link
                    className="underline cursor-pointer transition duration-200 text-pale-white hover:text-white hover:scale-105"
                    href={"/aboutUs"}
                    tabIndex={-1}
                  >
                    {"About Us"}
                  </Link>

                  <Link
                    className="underline cursor-pointer transition duration-200 text-pale-white hover:text-white hover:scale-105"
                    href={"#"}
                    onClick={openContact}
                    tabIndex={-1}
                  >
                    {"Contact Us"}
                  </Link>

                  {!userAvailable && (
                    <Link
                      className="underline cursor-pointer transition duration-200 text-pale-white hover:text-white hover:scale-105"
                      href={"#"}
                      onClick={openSendBrief}
                      tabIndex={-1}
                    >
                      {"Send Brief"}
                    </Link>
                  )}
                </div>
                <div
                  className="text-xs xl:text-sm text-pale-white tracking-tight">
                    <StringList items={links} hoverColor="text-white"></StringList>
                </div>
              </div>
            </RowFlex>
          </RowWrapper>
        </RowContainer>
      )
    )
  }
);

Panel.displayName = "Panel";
export {Panel};